import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useRef } from 'react'

import { useLocaleContext } from '@src/components/locale/locale.context'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'

import { withErrorBoundary } from '../errorBoundary'
import { PageLoader } from '../pageLoader'
import { Skeleton } from '../skeleton'

import { getExpressCheckoutSrc } from './express-checkout.helpers'
import styles from './express-checkout.module.scss'

interface ExpressCheckoutProps {
  showBothButtons: boolean
  showPaymentLoader?: boolean
  externalRef: React.MutableRefObject<HTMLIFrameElement | null>
  className?: string
  containerClass?: string
  showLoader?: boolean
}

function ExpressCheckout({
  showBothButtons,
  showPaymentLoader = false,
  externalRef,
  className,
  containerClass,
  showLoader = false,
}: ExpressCheckoutProps) {
  const { t } = useTranslation('common')
  const { locale } = useLocaleContext()
  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const { src: iframeSrc } = getExpressCheckoutSrc(showBothButtons, isMobile, locale)
  const ref = useRef<HTMLIFrameElement | null>(null)

  if (!iframeSrc) return null

  return (
    <div className={classNames(styles.expressCheckoutIframeContainer, containerClass)}>
      {showLoader && (
        <div className={styles.expressCheckoutLoaderContainer}>
          <Skeleton fullWidth height={isMobile ? 58 : 50} className={styles.expressCheckoutLoader} />
          {showBothButtons ? <Skeleton fullWidth height={50} className={styles.expressCheckoutLoader} /> : null}
        </div>
      )}
      <PageLoader
        showLoader={showPaymentLoader}
        title={t('your_payment_in_currently_being_processed')}
        description={t('do_not_refresh_or_close_your_browser')}
      />
      <iframe
        title={t('express_checkout_iframe')}
        ref={el => {
          ref.current = el
          externalRef.current = el
        }}
        src={iframeSrc}
        className={classNames(styles.expressCheckoutIframe, className)}
        data-testid="express-checkout-iframe"
      />
    </div>
  )
}

export default withErrorBoundary(ExpressCheckout)
